import {
  Button,
  ButtonGroup,
  Dialog,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  type FormSubmitHandler,
} from 'platform/components';
import {Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import i18n from '@dms/i18n';

import type {AbandonReason} from '../../model/auctionView.types';
import {useAuctionFormat} from './useAuctionFormat';

const REASONS = [
  'OWNER_DECLINED',
  'BUYER_WITHDREW',
  'VEHICLE_NOT_CONFORMING',
  'OTHER',
] as const satisfies readonly AbandonReason[];
const REASON_LABEL: Record<AbandonReason, string> = {
  OWNER_DECLINED: 'entity.auction.decision.reason.ownerDeclined',
  BUYER_WITHDREW: 'entity.auction.decision.reason.buyerWithdrew',
  VEHICLE_NOT_CONFORMING: 'entity.auction.decision.reason.vehicleNotConforming',
  OTHER: 'entity.auction.decision.reason.other',
};
// shortcut: the fees come from the prototype, the API does not return them; read them from the API when it does.
const CANCELLATION_FEE = {
  marketplace: {amount: '50', currency: 'EUR'},
  buyer: {amount: '250', currency: 'EUR'},
};

const abandonSchema = z.object({
  reason: z
    .enum(REASONS)
    .nullable()
    .refine((reason) => reason !== null, {
      message: i18n.t('general.validations.fieldIsRequired'),
    }),
});
// The input type keeps null, the empty value of the choice before the seller selects a reason.
type AbandonFormValues = z.input<typeof abandonSchema>;
const DEFAULT_VALUES: AbandonFormValues = {reason: null};

interface AbandonAuctionDialogProps {
  isOpen: boolean;
  // After the winner confirmation, the abandon also cancels the transaction with the buyer.
  isAwarded: boolean;
  onClose: () => void;
  // Resolves to false when the request fails. The caller shows the error, the dialog keeps the input.
  onAbandon: (reason: AbandonReason) => Promise<boolean>;
}

export function AbandonAuctionDialog(props: AbandonAuctionDialogProps) {
  const format = useAuctionFormat();

  const handleSubmit: FormSubmitHandler<AbandonFormValues> = async (values) => {
    if (values.reason !== null && (await props.onAbandon(values.reason))) {
      props.onClose();
    }
  };

  return (
    <Dialog
      isOpen={props.isOpen}
      size="small"
      title={i18n.t('entity.auction.decision.abandonTitle')}
      onClose={props.onClose}
      data-testid="auction-abandon-dialog"
    >
      <Form<AbandonFormValues>
        defaultValues={DEFAULT_VALUES}
        experimentalZodSchema={abandonSchema}
        onSubmit={handleSubmit}
      >
        {(control) => (
          <VStack spacing={4}>
            <Text color="secondary">
              {props.isAwarded
                ? i18n.t('entity.auction.abandon.consequenceAwarded', {
                    marketplaceFee: format.money(CANCELLATION_FEE.marketplace),
                    buyerFee: format.money(CANCELLATION_FEE.buyer),
                    defaultValue:
                      'The transaction with the confirmed buyer is cancelled. The cancellation costs {marketplaceFee} to the marketplace and {buyerFee} to the buyer, and it lowers the conversion rate.',
                  })
                : i18n.t('entity.auction.abandon.consequence', {
                    defaultValue:
                      'No purchase or sale contract will be closed. The auction and offers stay in history.',
                  })}
            </Text>
            <FormField
              control={control}
              name="reason"
              type="choice"
              label={i18n.t('entity.auction.abandon.reason', {defaultValue: 'Reason'})}
              isRequired
              menuInPortal
              options={REASONS.map((value) => ({value, label: i18n.t(REASON_LABEL[value])}))}
              data-testid="auction-abandon-reason"
            />
            <DialogFooter>
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('entity.auction.abandon.keepWorking', {
                    defaultValue: 'Keep working',
                  })}
                  variant="secondary"
                  onClick={props.onClose}
                  data-testid="auction-abandon-keep"
                />
                <FormButton
                  control={control}
                  type="submit"
                  variant="danger"
                  title={i18n.t('entity.auction.decision.abandon')}
                  data-testid="auction-abandon-confirm"
                />
              </ButtonGroup>
            </DialogFooter>
          </VStack>
        )}
      </Form>
    </Dialog>
  );
}
